import { Box } from "@mui/material";
import { useEffect, useState } from "react";
import SearchBar from "../../components/SearchBar";
import TableComponent from "../../components/TableComponent";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { CommonBody, PaymentResponseType } from "../../types/General";
import {
  usePartnerPaymentsMutation,
  useUserPaymentsMutation,
} from "../../service/payments";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { PAYMENT_STATUS_LABELS, USER_TYPE_SLUGS } from "../../utils/enums";
import { errorToast } from "../../helpers";
import { isValidInput } from "../../utils/validations";
import moment from "moment";
import { PriceFormatter } from "../../utils/commoFunctions";

type props = {
  id: string | undefined;
  type: string | null | undefined;
};

const IndividualPaymentHistory = ({ id, type }: props) => {
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");

  const [rows, setRows] = useState<PaymentResponseType[]>([]);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [partnerPayments] = usePartnerPaymentsMutation();
  const [individualPayments] = useUserPaymentsMutation();

  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<number>(1);

  const handlePageChange = (event: any, newPage: number) => {
    setPage(newPage + 1);
  };

  const getPaymentHistory = async () => {
    setLoading(true);
    try {
      const body = {
        start: page,
        length: 10,
        search: debouncedSearchTerm.trim(),
      };

      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      let response;
      if (type === USER_TYPE_SLUGS.INDIVIDUAL) {
        response = await individualPayments({
          body: encryptedBody,
          id: Number(id),
        }).unwrap();
      } else {
        response = await partnerPayments({
          body: encryptedBody,
          id: Number(id),
        }).unwrap();
      }

      setLoading(false);

      if (response?.status === 200) {
        setRows(response?.data?.response_object || []);
        setCount(response?.data?.total_records);
      } else {
        setRows([]);
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
      setLoading(false);
    }
  };

  const columns: GridColDef<(typeof rows)[number]>[] = [
    {
      flex: 1,
      minWidth: 200,
      field: "paymentDate",
      headerName: "Payment Date",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div className="user_block">
            <p>
              {row?.created_at
                ? moment(row?.created_at).format("DD MMM YYYY hh:mm a")
                : "-"}
            </p>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 150,
      field: "applicationId",
      headerName: "Application Id",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div className="user_block">
            <p>{row?.application?.apk_id || "-"}</p>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 300,
      field: "applicantDetails",
      headerName: "User Details",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div className="user_block">
            <figure onClick={() => navigate(`/users/individual/${row?.id}`)}>
              <img
                src={
                  row?.user?.profile_picture?.media_url ||
                  "/static/images/user_placeholder.png"
                }
                alt=""
              />
            </figure>
            <p>
              <b>{row?.user?.name || "-"}</b> {row?.user?.country_code}{" "}
              {row?.user?.phone_no || "-"} <br />
              {row?.user?.email || "-"}
            </p>
          </div>
        );
      },
    },
    ...(type === USER_TYPE_SLUGS.COMPANY ||
    type === USER_TYPE_SLUGS.FREELANCER_AGENT
      ? []
      : [
          {
            flex: 1,
            minWidth: 300,
            field: "applierDetails",
            headerName: "Paid to",
            editable: false,
            sortable: false,
            renderCell: ({ row }: GridRenderCellParams<any>) => {
              return (
                <div className="user_block">
                  {row?.applier?.email === row?.user?.email ? null : (
                    <figure
                    // onClick={() =>
                    //   navigate(
                    //     `/profile-detail/${row?.applier?.user_role_type === 6 ? "company" : "freelancer-agent"}/${row?.id}`
                    //   )
                    // }
                    >
                      <img
                        src={
                          row?.applier?.profile_picture?.media_url ||
                          "/static/images/user_placeholder.png"
                        }
                        alt=""
                      />
                    </figure>
                  )}
                  {row?.applier?.email === row?.user?.email ? (
                    <p>ADMIN</p>
                  ) : (
                    <p>
                      <b>{row?.applier?.name || "-"}</b>{" "}
                      {row?.applier?.country_code}{" "}
                      {row?.applier?.phone_no || ""} <br />
                      {row?.applier?.email || "-"}
                    </p>
                  )}
                </div>
              );
            },
          },
        ]),

    // {
    //   flex: 1,
    //   minWidth: 150,
    //   field: "invoiceNumber",
    //   headerName: "Invoice Number",
    //   editable: false,
    //   sortable: false,
    //   renderCell: () => {
    //     return (
    //       <div className="user_block">
    //         <p>WIZ-565744</p>
    //       </div>
    //     );
    //   },
    // },
    {
      flex: 1,
      minWidth: 150,
      field: "paymentMode",
      headerName: "Payment Mode",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div className="user_block">
            <p>{row?.plan_mode?.title || "-"}</p>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 150,
      field: "paymentType",
      headerName: "Payment Type",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        const partNumber = row?.plan_part_no?.part_number;
        return (
          <div className="user_block">
            <p>
              {partNumber !== null && partNumber !== undefined
                ? PAYMENT_STATUS_LABELS[row?.plan_part_no?.part_number]
                : "-"}
            </p>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 150,
      field: "amountPaid",
      headerName: "Amount Paid",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div className="user_block">
            <p>
              {PriceFormatter(row?.payment_amt_after_vat?.toString()) || "-"}
            </p>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 150,
      field: "vat",
      headerName: "VAT",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div className="user_block">
            <p>{PriceFormatter(row?.vat?.toString()) || "-"}</p>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 150,
      field: "remainingAmount",
      headerName: "Remaining Amount",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div className="user_block">
            <p>{PriceFormatter(row?.remaining_amt?.toString()) || "-"}</p>
          </div>
        );
      },
    },

    // {
    //   flex: 0.1,
    //   minWidth: 80,
    //   field: "action",
    //   headerName: "Action",
    //   editable: false,
    //   sortable: false,
    //   renderCell: ({ row }) => {
    //     return (
    //       <div className="table_actions">
    //         <Box>
    //           <IconButton
    //             aria-controls={open1 ? "basic-menu1" : undefined}
    //             aria-haspopup="true"
    //             aria-expanded={open1 ? "true" : undefined}
    //             onClick={(event) => handleClick1(event, row?.id)}
    //           >
    //             <img src="/static/images/more_icon.svg" alt="" />
    //           </IconButton>
    //           <Menu
    //             className="setting_menu"
    //             id="basic-menu1"
    //             anchorEl={anchorEl1}
    //             open={open1}
    //             onClose={handleClose1}
    //             MenuListProps={{
    //               "aria-labelledby": "basic-button1",
    //             }}
    //             PaperProps={{
    //               elevation: 0,
    //               sx: {
    //                 overflow: "visible",
    //                 boxShadow: "0px 4px 20px 0px rgba(0, 0, 0, 0.05)",
    //                 borderRadius: "8px",
    //                 mt: 1,
    //               },
    //             }}
    //             transformOrigin={{ horizontal: "right", vertical: "top" }}
    //             anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
    //           >
    //             <MenuItem onClick={() => navigate(`view/${selectedId}`)}>
    //               View
    //             </MenuItem>
    //           </Menu>
    //         </Box>
    //       </div>
    //     );
    //   },
    // },
  ];

  useEffect(() => {
    getPaymentHistory();
  }, [debouncedSearchTerm, page]);

  return (
    <div className="cards">
      <Box className="custom_tabs" sx={{ width: "100%" }}>
        <div className="table_header">
          <div className="left_s">
            <Box className="search_box">
              <SearchBar
                searchTerm={searchTerm}
                placeholder="Search"
                setDebouncedSearchTerm={setDebouncedSearchTerm}
                value={searchTerm}
                onCross={() => setSearchTerm("")}
                onChange={(val: any) => {
                  if (isValidInput(val.target.value)) {
                    setSearchTerm(val.target.value);
                  }
                }}
              />
            </Box>
          </div>
        </div>

        <div className="sorting_table">
          <TableComponent
            columns={columns}
            rows={rows}
            count={count}
            page={page}
            onPageChange={handlePageChange}
            loading={loading}
          />
        </div>
      </Box>
    </div>
  );
};

export default IndividualPaymentHistory;
